<template>
  <div class="BaseTemplate bg-white no-wrap full-width column full-height q-mx-none">
    <BaseTemplateHeader @toggle-layer-drawer="state.isLayerOpen = !state.isLayerOpen" />
    <div class="relative-position flex no-wrap flex-1 overflow-hidden">
      <BaseTemplateLayers
        v-click-outside="handleHideLayer"
        class="BaseTemplate__left-side"
        :class="{ 'BaseTemplate__left-side--hidden': $q.screen.lt.md && !state.isLayerOpen }"
      />
      <BaseTemplateMain class="BaseTemplate__main-side" />
      <BaseTemplateItemSetting class="BaseTemplate__right-side" />

      <div class="BaseTemplate__toolbar row items-center justify-center q-px-16 q-py-8">
        <div class="row items-center gap-sm">
          <q-btn
            class="BaseTemplate__toolbar-btn bg-white shadow-1 rounded-12"
            :class="{ 'BaseTemplate__toolbar-btn--active': templateStore.isMultiSelectMode }"
            flat
            dense
            round
            @click="templateStore.isMultiSelectMode = !templateStore.isMultiSelectMode"
          >
            <k-icon
              name="multi-selection"
              size="24px"
              :color="templateStore.isMultiSelectMode ? 'space-b-d' : 'cool-gray-100'"
            />
            <k-tooltip position="top" :label="t('toolbar.multi_select')" />
          </q-btn>

          <q-btn
            class="BaseTemplate__toolbar-btn bg-white shadow-1 rounded-12"
            flat
            dense
            round
            :disable="!templateStore.canUndo"
            @click="templateStore.undo()"
          >
            <k-icon
              name="arrow-curve-left"
              size="24px"
              :color="templateStore.canUndo ? 'cool-gray-100' : 'cool-gray-50'"
            />
            <k-tooltip position="top" :label="t('toolbar.undo')" />
          </q-btn>

          <q-btn
            class="BaseTemplate__toolbar-btn bg-white shadow-1 rounded-12"
            flat
            dense
            round
            :disable="!templateStore.canRedo"
            @click="templateStore.redo()"
          >
            <k-icon
              name="arrow-curve-right"
              size="24px"
              :color="templateStore.canRedo ? 'cool-gray-100' : 'cool-gray-50'"
            />
            <k-tooltip position="top" :label="t('toolbar.redo')" />
          </q-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted } from 'vue'
import vClickOutside from 'src/shared/directives/ClickOutside'
import { useTemplateStore } from 'src/stores'
import translate from 'src/common/helpers/language/translate'
import translationObject from './translate/baseTemplate.translate'
import useBaseTemplateHook from './BaseTemplateHook'
import BaseTemplateHeader from './components/header/BaseTemplateHeader'
import BaseTemplateLayers from './components/layers/BaseTemplateLayers'
import BaseTemplateItemSetting from './components/setting/BaseTemplateItemSetting'

defineOptions({ name: 'BaseTemplate' })

const { state, handleHideLayer } = useBaseTemplateHook()
const templateStore = useTemplateStore()
const { t } = translate(translationObject)

function onKeyDown(e) {
  const tag = e.target?.tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable) return
  if ((e.ctrlKey || e.metaKey) && e.code === 'KeyZ') {
    e.preventDefault()
    if (e.shiftKey) { if (templateStore.canRedo) templateStore.redo() }
    else { if (templateStore.canUndo) templateStore.undo() }
  }
}

onMounted(() => window.addEventListener('keydown', onKeyDown))
onUnmounted(() => window.removeEventListener('keydown', onKeyDown))
</script>

<style lang="sass" scoped>
@import "src/shared/styles/variables.sass"
@import "src/shared/styles/mixins.sass"

.BaseTemplate
  @include element('left-side')
    max-width: 380px
    width: 100%
    @include state-modifier('hidden')
      transform: translateX(-100%)
  @include element('main-side')
    min-width: 755px
  @include element('right-side')
    min-width: 340px
    max-width: 340px
    width: 100%
  @include element('toolbar')
    position: absolute
    bottom: 16px
    left: 50%
    transform: translateX(-50%)
    z-index: 10
    pointer-events: all
  @include element('toolbar-btn')
    min-width: 40px
    min-height: 40px
    :deep(.q-focus-helper)
      display: none !important
    &:hover
      background: var(--space-b-10) !important
    @include state-modifier('active')
      border: 1px solid var(--space-b-50)
      background: var(--space-b-20) !important
      &:hover
        background: var(--space-b-20) !important
</style>
